/* Collection mosaic — sections/custom-collection-mosaic.liquid.
   Fits the heading to its cell, tints the faint photos behind the heading and
   link, and slides products through the photo cells one at a time: 1 s slide
   from a random side, 1 s rest, every visible cell once per round in random
   order. The cell under the pointer or focus never moves; nothing moves while
   the grid is off screen, the tab is hidden, or the visitor prefers reduced
   motion. Products beyond the visible cells wait in a queue; cells hidden by
   the tablet/mobile layout hand their products to the visible ones. */
(() => {
  if (customElements.get('collection-mosaic')) return;

  const DURATION = 1000;
  const REST = 1000;
  const DIRS = [[100, 0], [-100, 0], [0, 100], [0, -100]]; // enter from right / left / bottom / top
  const EASING = 'cubic-bezier(.65, 0, .35, 1)';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  let filterSeq = 0;

  const shuffle = (a) => {
    for (let i = a.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const sized = (src, w) => src.replace(/([?&])width=\d+/, `$1width=${w}`);

  /* Any CSS colour (rgb(), color(srgb …) from color-mix) → [r, g, b] in 0..1 */
  const rgbOf = (cssColor) => {
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = cssColor;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return [r / 255, g / 255, b / 255];
  };

  class CollectionMosaic extends HTMLElement {
    connectedCallback() {
      const poolEl = this.querySelector('[data-mosaic-pool]');
      this.pool = poolEl ? JSON.parse(poolEl.textContent) : [];
      this.real = [...this.querySelectorAll(':scope > .custom-mosaic__cell')];
      this.ghosts = [...this.querySelectorAll('.custom-mosaic__cell--ghost')];
      this.current = this.real.map((c, i) => (c.querySelector('.custom-mosaic__tile') ? this.pool[i] : null));
      this.queue = shuffle(this.pool.slice(this.current.filter(Boolean).length));
      this.cells = [...this.real, ...this.ghosts];
      this.ghostTurn = 0;
      this.bag = [];
      this.last = -1;

      this.fitHeading();
      this.fillGhosts();

      if (this.dataset.animate !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      new IntersectionObserver(([e]) => {
        this.visible = e.isIntersecting;
        if (this.visible) this.schedule();
      }, { threshold: 0.25 }).observe(this);
      document.addEventListener('visibilitychange', () => !document.hidden && this.schedule());
      ['(max-width: 749px)', '(max-width: 989px)'].forEach((q) =>
        matchMedia(q).addEventListener('change', () => {
          this.bag = [];
          this.schedule();
        })
      );
    }

    /* ---- heading: the largest size that fits 84 % of the width and 62 % of the height ---- */
    fitHeading() {
      const cell = this.querySelector('.custom-mosaic__heading');
      const title = cell && cell.querySelector('.custom-mosaic__title');
      if (!title) return;
      const fit = () => {
        const maxH = cell.clientHeight * 0.62;
        let lo = 14;
        let hi = Math.max(lo, maxH);
        for (let n = 0; n < 12; n++) {
          const mid = (lo + hi) / 2;
          title.style.fontSize = `${mid}px`;
          if (title.scrollWidth <= title.clientWidth + 1 && title.offsetHeight <= maxH) lo = mid;
          else hi = mid;
        }
        title.style.fontSize = `${Math.floor(lo)}px`;
      };
      new ResizeObserver(fit).observe(cell);
      if (document.fonts) document.fonts.ready.then(fit);
    }

    /* ---- ghosts: duotone tint from each cell's own colour, then fill with waiting products ---- */
    fillGhosts() {
      const strength = Number(this.dataset.tint) / 100;
      if (!this.ghosts.length || !strength || !this.pool.length) return;

      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('width', '0');
      svg.setAttribute('height', '0');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.position = 'absolute';
      [['heading', '.custom-mosaic__heading'], ['link', '.custom-mosaic__link']].forEach(([key, sel]) => {
        const cell = this.querySelector(sel);
        if (!cell) return;
        const id = `mosaic-tint-${++filterSeq}`;
        const light = rgbOf(getComputedStyle(cell).backgroundColor);
        const table = (c) => `${(c * (1 - strength)).toFixed(3)} ${c.toFixed(3)}`;
        svg.insertAdjacentHTML(
          'beforeend',
          `<filter id="${id}" color-interpolation-filters="sRGB">
            <feColorMatrix type="saturate" values="0"/>
            <feComponentTransfer>
              <feFuncR type="table" tableValues="${table(light[0])}"/>
              <feFuncG type="table" tableValues="${table(light[1])}"/>
              <feFuncB type="table" tableValues="${table(light[2])}"/>
            </feComponentTransfer>
          </filter>`
        );
        this.style.setProperty(`--mosaic-tint-${key}`, `url(#${id})`);
      });
      this.appendChild(svg);

      this.ghosts.forEach((g) => g.appendChild(this.tile(this.ghostPick(), true)));
      this.classList.add('is-tinted');
    }

    /* Ghosts preview waiting products without taking them from the queue */
    ghostPick() {
      if (this.queue.length) return this.queue[this.ghostTurn++ % this.queue.length];
      return this.pool[(Math.random() * this.pool.length) | 0];
    }

    tile(p, ghost) {
      const t = document.createElement(ghost ? 'span' : 'a');
      t.className = 'custom-mosaic__tile';
      if (!ghost) t.href = p.url;
      const img = document.createElement('img');
      img.src = p.img;
      img.srcset = `${sized(p.img, 300)} 300w, ${sized(p.img, 450)} 450w, ${p.img} 600w`;
      img.sizes = '(min-width: 990px) 240px, (min-width: 750px) 25vw, 33vw';
      img.alt = ghost ? '' : p.alt;
      img.width = 600;
      img.height = 600;
      t.appendChild(img);
      return t;
    }

    shown(i) {
      return this.cells[i].offsetParent !== null; // false for cells the layout hides
    }

    isGhost(i) {
      return i >= this.real.length;
    }

    nextCell() {
      const usable = (i) => this.shown(i) && (this.isGhost(i) || this.current[i]);
      for (let tries = 0; tries < this.cells.length * 2; tries++) {
        if (!this.bag.length) this.bag = shuffle([...this.cells.keys()].filter(usable)).filter((i) => i !== this.last);
        if (!this.bag.length) return -1;
        const i = this.bag.shift();
        if (!usable(i)) continue; // layout changed since the round began
        const cell = this.cells[i];
        if (!cell.matches(':hover') && !cell.contains(document.activeElement)) return i;
        this.bag.push(i);
      }
      return -1;
    }

    async slide(cell, incoming, ghost) {
      /* Decode the tile's own image first, so the browser fetches only the
         srcset width it picks for this cell (no separate 600px preload). */
      const newTile = this.tile(incoming, ghost);
      await newTile.querySelector('img').decode().catch(() => {});
      const oldTile = cell.querySelector('.custom-mosaic__tile');
      const [dx, dy] = DIRS[(Math.random() * 4) | 0];
      cell.appendChild(newTile);
      const opts = { duration: DURATION, easing: EASING, fill: 'forwards' };
      if (oldTile) oldTile.animate([{ transform: 'none' }, { transform: `translate(${-dx}%, ${-dy}%)` }], opts);
      await newTile.animate([{ transform: `translate(${dx}%, ${dy}%)` }, { transform: 'none' }], opts).finished;
      if (oldTile) oldTile.remove();
    }

    async step() {
      this.timer = null;
      if (this.running || !this.visible || document.hidden) return;
      const hidden = [...this.real.keys()].filter((i) => !this.shown(i) && this.current[i]);
      if (!this.queue.length && !hidden.length) return; // everything is already on screen
      const i = this.nextCell();
      if (i < 0) return this.schedule();
      this.running = true;
      try {
        if (this.isGhost(i)) {
          await this.slide(this.cells[i], this.ghostPick(), true);
        } else {
          let spare = -1;
          if (hidden.length && Math.random() * (hidden.length + this.queue.length) < hidden.length) {
            spare = hidden[(Math.random() * hidden.length) | 0];
          }
          const incoming = spare >= 0 ? this.current[spare] : this.queue.shift();
          await this.slide(this.cells[i], incoming, false);
          if (spare >= 0) {
            /* the outgoing product parks in the hidden cell it came from */
            this.cells[spare].replaceChildren(this.tile(this.current[i]));
            this.current[spare] = this.current[i];
          } else {
            this.queue.push(this.current[i]);
          }
          this.current[i] = incoming;
        }
        this.last = i;
      } finally {
        this.running = false;
      }
      this.schedule();
    }

    schedule() {
      if (!this.timer && !this.running) this.timer = setTimeout(() => this.step(), REST);
    }
  }

  customElements.define('collection-mosaic', CollectionMosaic);
})();
